import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import EditableCell from '../common/EditableCell';
import ColumnFilter from '../common/ColumnFilter';
import RentalsExportModal from './RentalsExportModal';
import RentalsUploadModal from './RentalsUploadModal';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';
import RentalsAnalyticsDashboard from './RentalsAnalyticsDashboard';

const COLUMNS = [
  { key: 'description', label: 'Description', width: 200, type: 'text' },
  { key: 'total_hours', label: 'Total Hours', width: 100, type: 'number' },
  { key: 'total_install', label: 'Total Install', width: 100, type: 'number' },
  { key: 'hourly', label: 'Hourly', width: 90, type: 'number' },
  { key: 'daily', label: 'Daily', width: 90, type: 'number' },
  { key: 'weekly', label: 'Weekly', width: 90, type: 'number' },
  { key: 'monthly', label: 'Monthly', width: 90, type: 'number' },
  { key: 'days_wk', label: 'Days/Wk', width: 80, type: 'number' },
  { key: 'hrs_day', label: 'Hrs/Day', width: 80, type: 'number' },
  { key: 'qty', label: 'Qty', width: 70, type: 'number' },
  { key: 'period_type', label: 'Period Type', width: 100, type: 'text' },
  { key: 'duration', label: 'Duration', width: 90, type: 'number' },
  { key: 'basic_rental', label: 'Basic Rental', width: 110, type: 'number' },
  { key: 'fuel_factor', label: 'Fuel Factor', width: 100, type: 'number' },
  { key: 'fuel_cost', label: 'Fuel Cost', width: 100, type: 'number' },
  { key: 'mob_dem', label: 'Mob/Dem', width: 100, type: 'number' },
  { key: 'surcharge_factor', label: 'Surch. Factor', width: 110, type: 'number' },
  { key: 'surcharge', label: 'Surcharge', width: 100, type: 'number' },
  { key: 'purchase', label: 'Purchase', width: 100, type: 'number' },
  { key: 'total_cost', label: 'Total Cost', width: 110, type: 'number' },
  { key: 'site_expense', label: 'Site Expense', width: 110, type: 'number' },
  { key: 'source', label: 'Source', width: 100, type: 'text' },
  { key: 'trade', label: 'Trade', width: 100, type: 'text' },
  { key: 'from_out', label: 'From Out', width: 100, type: 'text' },
  { key: 'subject_to', label: 'Subject To', width: 100, type: 'text' },
  { key: 'comments', label: 'Comments', width: 200, type: 'text' }
];

const RentalsLog = () => {
  const { instance, accounts } = useMsal();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [columnFilters, setColumnFilters] = useState({});

  const fetchRentalsRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchRentalsRef.current) fetchRentalsRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('rentalsLog', 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);
  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  const fetchRentals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/cost-breakdown/rentals');
      if (response.data.success) {
        setRentals(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchRentals(); }, [fetchRentals]);
  useEffect(() => { fetchRentalsRef.current = fetchRentals; }, [fetchRentals]);

  const handleSaveCell = async (id, field, value) => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/cost-breakdown/rentals/${id}`, { [field]: value }, syncHeaders());
      setRentals(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
    } catch (err) {
      console.error('Error saving:', err);
      alert('Failed to save');
    }
  };

  const handleAddNewRow = async () => {
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/cost-breakdown/rentals', newRow, syncHeaders());
      if (response.data.success) {
        setRentals(prev => [response.data.data, ...prev]);
        setNewRow({});
        setIsAddingNew(false);
      }
    } catch (err) {
      alert('Failed to add: ' + err.message);
    }
  };

  const handleDeleteRow = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/cost-breakdown/rentals/${id}`, null, syncHeaders());
      setRentals(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleFilterChange = (columnKey, values) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: values }));
  };

  const getUniqueValues = (columnKey) => {
    const values = new Set();
    let hasBlank = false;
    rentals.forEach(r => {
      const val = r[columnKey];
      if (val === null || val === undefined || val === '') { hasBlank = true; }
      else { values.add(String(val)); }
    });
    const sorted = Array.from(values).sort();
    if (hasBlank) sorted.push('(Blank)');
    return sorted;
  };

  const filteredAndSorted = React.useMemo(() => {
    let result = [...rentals];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(r => COLUMNS.some(c => String(r[c.key] || '').toLowerCase().includes(lower)));
    }
    Object.keys(columnFilters).forEach(key => {
      if (columnFilters[key] && columnFilters[key].length > 0) {
        result = result.filter(r => {
          const val = r[key];
          const isBlank = val === null || val === undefined || val === '';
          if (isBlank) return columnFilters[key].includes('(Blank)');
          return columnFilters[key].includes(String(val));
        });
      }
    });
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key], bVal = b[sortConfig.key];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [rentals, searchTerm, columnFilters, sortConfig]);

  if (loading) return <div style={{ padding: '20px', color: '#fff' }}>Loading...</div>;
  if (error) return <div style={{ padding: '20px', color: '#ff6b6b' }}>Error: {error}</div>;

  return (
    <div style={{ padding: '20px', backgroundColor: '#1a1a2e', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          📄 Rentals <span style={{ backgroundColor: '#4a4a5a', padding: '2px 10px', borderRadius: '12px', fontSize: '14px' }}>{filteredAndSorted.length} records</span>
        </h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {/* Active Viewers - Real-time presence indicator */}
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #3a3a4a', backgroundColor: '#2a2a3a', color: '#fff', width: '200px' }} />
          <button onClick={() => setShowAnalytics(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📊 Analytics</button>
          <button onClick={() => setIsAddingNew(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>➕ Add New</button>
          <button onClick={() => setShowUploadModal(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📥 Import</button>
          <button onClick={() => setShowExportModal(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📤 Export</button>
        </div>
      </div>

      <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 150px)', position: 'relative' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>
              <th style={{ position: 'sticky', top: 0, left: 0, zIndex: 3, backgroundColor: '#343a40', color: '#fff', padding: '10px 8px', minWidth: '80px', borderBottom: '2px solid #4a4a5a' }}>Actions</th>
              {COLUMNS.map(col => (
                <th key={col.key} style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#343a40', color: '#fff', padding: '10px 8px', minWidth: col.width, borderBottom: '2px solid #4a4a5a', cursor: 'pointer', textAlign: 'left' }}
                  onClick={() => handleSort(col.key)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {col.label} {sortConfig.key === col.key && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    <ColumnFilter values={getUniqueValues(col.key)} selectedValues={columnFilters[col.key] || []} onChange={(vals) => handleFilterChange(col.key, vals)} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isAddingNew && (
              <tr style={{ backgroundColor: '#2d4a2d' }}>
                <td style={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#2d4a2d', padding: '8px' }}>
                  <button onClick={handleAddNewRow} style={{ padding: '4px 8px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '3px', marginRight: '4px', cursor: 'pointer' }}>Save</button>
                  <button onClick={() => { setIsAddingNew(false); setNewRow({}); }} style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Cancel</button>
                </td>
                {COLUMNS.map(col => (
                  <td key={col.key} style={{ padding: '4px' }}>
                    <input type={col.type === 'number' ? 'number' : 'text'} value={newRow[col.key] || ''} onChange={(e) => setNewRow(p => ({ ...p, [col.key]: e.target.value }))}
                      style={{ width: '100%', padding: '6px', backgroundColor: '#1a1a2e', color: '#fff', border: '1px solid #4a4a5a', borderRadius: '3px' }} />
                  </td>
                ))}
              </tr>
            )}
            {filteredAndSorted.map(row => (
              <tr key={row.id} style={{ backgroundColor: '#2a2a3a' }}>
                <td style={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#2a2a3a', padding: '8px', borderBottom: '1px solid #3a3a4a' }}>
                  <button onClick={() => handleDeleteRow(row.id)} style={{ padding: '4px 8px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>🗑</button>
                </td>
                {COLUMNS.map(col => (
                  <td key={col.key} style={{ padding: '4px', borderBottom: '1px solid #3a3a4a' }}>
                    <EditableCell value={row[col.key]} row={row} column={col} onSave={handleSaveCell} theme="dark" accentColor="#6c757d" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showExportModal && <RentalsExportModal onClose={() => setShowExportModal(false)} data={rentals} />}
      {showUploadModal && <RentalsUploadModal onClose={() => setShowUploadModal(false)} onUploadComplete={fetchRentals} />}
      
      {/* Analytics Dashboard */}
      {showAnalytics && (
        <RentalsAnalyticsDashboard
          data={rentals}
          onClose={() => setShowAnalytics(false)}
        />
      )}
    </div>
  );
};

export default RentalsLog;
